// shared/types/evals.ts
//
// Single source of truth for the EvalsStrip view-model.
// Plain TypeScript only — no Convex generic types (Id<>, Doc<>) — so this
// module is consumable from both Convex backend and the React frontend.
//
// Hard Invariant 6: shared/ is Convex-free.
//
// @see specs/prd.md FR-009, FR-010, Section 7.4

/** Evaluation status classification for colour-coding. */
export type EvalStatus = "success" | "warning" | "danger";

/** Thresholds for classifying metrics into status bands. */
export interface EvalThresholds {
  /** Goal success rate: danger below this value. Default 0.70. */
  goalSuccessDanger: number;
  /** Goal success rate: warning below this value. Default 0.80. */
  goalSuccessWarning: number;
  /** Tool accuracy rate: danger below this value. Default 0.70. */
  toolAccuracyDanger: number;
  /** Tool accuracy rate: warning below this value. Default 0.85. */
  toolAccuracyWarning: number;
  /** Failure coverage: danger below this value. Default 0.60. */
  failureCoverageDanger: number;
  /** Failure coverage: warning below this value. Default 0.80. */
  failureCoverageWarning: number;
}

/** Default thresholds matching alert A16-A18 definitions. */
export const DEFAULT_EVAL_THRESHOLDS: EvalThresholds = {
  goalSuccessDanger: 0.70,
  goalSuccessWarning: 0.80,
  toolAccuracyDanger: 0.70,
  toolAccuracyWarning: 0.85,
  failureCoverageDanger: 0.60,
  failureCoverageWarning: 0.80,
};

/**
 * A single rollup window as returned by the agentEvals composite.
 *
 * All rate fields are 0-1 (i.e. 0.85 means 85%).
 */
export interface EvalRollup {
  /** Goal success rate over the window. */
  goalSuccessRate: number;
  /** Tool selection accuracy rate over the window. */
  toolAccuracyRate: number;
  /** Failure detection coverage over the window. */
  failureCoverage: number;
  /** ISO-8601 window start timestamp. */
  windowStart: string;
  /** ISO-8601 window end timestamp. */
  windowEnd: string;
  /** Total evaluation runs in the window. */
  totalRuns: number;
}

/**
 * View-model consumed by useAgentEvals hook and EvalsStrip component.
 *
 * The latest field is null when no rollups have been computed yet.
 */
export interface EvalsView {
  /** The most recent rollup window, or null if no data. */
  latest: EvalRollup | null;
}
